import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { AppLayout } from '@/components/AppLayout';
import { DashboardSkeleton } from '@/components/skeletons';
import { api } from '@/lib/api';
import { useTenant } from '@/hooks/useTenant';
import {
  TrendingUp,
  Users,
  CheckCircle,
  Calendar,
  ChevronRight,
  UserPlus,
  Building2,
  Contact,
  Target,
  Plus,
} from 'lucide-react';

// Helper functions
function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatRelativeDate(date: string): string {
  const now = new Date();
  const then = new Date(date);
  const diffMs = now.getTime() - then.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return then.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function formatShortDate(date: string): string {
  return new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function getInitials(firstName: string, lastName: string): string {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
}

const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
  'bg-pink-100 text-pink-700',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
}

const stageConfig: Record<string, { label: string; dotClass: string; bgClass: string }> = {
  qualification: {
    label: 'Qualify',
    dotClass: 'stage-dot-qualification',
    bgClass: 'stage-qualification',
  },
  needs_analysis: {
    label: 'Analyze',
    dotClass: 'stage-dot-needs_analysis',
    bgClass: 'stage-needs_analysis',
  },
  proposal: { label: 'Propose', dotClass: 'stage-dot-proposal', bgClass: 'stage-proposal' },
  negotiation: {
    label: 'Negotiate',
    dotClass: 'stage-dot-negotiation',
    bgClass: 'stage-negotiation',
  },
  closed_won: { label: 'Won', dotClass: 'stage-dot-closed_won', bgClass: 'stage-closed_won' },
  closed_lost: { label: 'Lost', dotClass: 'stage-dot-closed_lost', bgClass: 'stage-closed_lost' },
};

const statusConfig: Record<string, { label: string; class: string }> = {
  new: { label: 'New', class: 'status-new' },
  contacted: { label: 'Contacted', class: 'status-contacted' },
  qualified: { label: 'Qualified', class: 'status-qualified' },
  unqualified: { label: 'Unqualified', class: 'status-unqualified' },
  converted: { label: 'Converted', class: 'status-converted' },
};

const sourceLabels: Record<string, string> = {
  website: 'Website',
  referral: 'Referral',
  cold_call: 'Cold Call',
  trade_show: 'Trade Show',
  partner: 'Partner',
  other: 'Other',
};

export function HomePage() {
  const currentMonth = new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const { isTenantContextReady, activeTenantId, mode } = useTenant();

  // Fetch opportunities for pipeline metrics
  const { data: opportunitiesData, isLoading: loadingOpps } = useQuery({
    queryKey: ['opportunities', 'dashboard', mode, activeTenantId],
    queryFn: () => api.getOpportunities({ pageSize: 100 }),
    enabled: isTenantContextReady,
  });

  // Fetch leads for recent leads
  const { data: leadsData, isLoading: loadingLeads } = useQuery({
    queryKey: ['leads', 'dashboard', mode, activeTenantId],
    queryFn: () => api.getLeads({ pageSize: 10 }),
    enabled: isTenantContextReady,
  });

  const opportunities = opportunitiesData?.data || [];
  const leads = leadsData?.data || [];

  // Calculate metrics
  const openOpps = opportunities.filter(o => !['closed_won', 'closed_lost'].includes(o.stage));
  const totalPipeline = openOpps.reduce((sum, o) => sum + (o.amount || 0), 0);
  const weightedPipeline = openOpps.reduce(
    (sum, o) => sum + ((o.amount || 0) * (o.probability || 0)) / 100,
    0
  );
  const openLeadsCount = leads.filter(l => !['converted', 'unqualified'].includes(l.status)).length;

  // Win rate calculation (last 90 days)
  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
  const closedOpps = opportunities.filter(
    o => ['closed_won', 'closed_lost'].includes(o.stage) && new Date(o.updatedAt) > ninetyDaysAgo
  );
  const wonOpps = closedOpps.filter(o => o.stage === 'closed_won');
  const winRate =
    closedOpps.length > 0 ? Math.round((wonOpps.length / closedOpps.length) * 100) : 0;

  // Closing this month
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const monthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const closingThisMonth = openOpps.filter(o => {
    const closeDate = new Date(o.expectedCloseDate);
    return closeDate >= monthStart && closeDate <= monthEnd;
  });

  // Pipeline by stage
  const stageOrder = ['qualification', 'needs_analysis', 'proposal', 'negotiation'];
  const pipelineByStage = stageOrder.map(stage => ({
    stage,
    ...stageConfig[stage],
    amount: openOpps.filter(o => o.stage === stage).reduce((sum, o) => sum + (o.amount || 0), 0),
    count: openOpps.filter(o => o.stage === stage).length,
  }));

  // Recent leads (new leads)
  const newLeadsCount = leads.filter(l => l.status === 'new').length;
  const recentLeads = leads.slice(0, 5);

  const isLoading = loadingOpps || loadingLeads;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Page Header with Quick Actions */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Dashboard</h1>
            <p className="text-muted-foreground mt-1">Your sales overview for {currentMonth}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link
              to="/leads"
              search={{ create: 'true' }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground font-medium text-sm hover:bg-primary/90 transition-colors"
            >
              <UserPlus className="w-4 h-4" />
              <span>New Lead</span>
            </Link>
            <Link
              to="/accounts"
              search={{ create: 'true' }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border text-foreground font-medium text-sm hover:bg-muted transition-colors"
            >
              <Building2 className="w-4 h-4" />
              <span>New Account</span>
            </Link>
            <Link
              to="/contacts"
              search={{ create: 'true' }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border text-foreground font-medium text-sm hover:bg-muted transition-colors"
            >
              <Contact className="w-4 h-4" />
              <span>New Contact</span>
            </Link>
            <Link
              to="/opportunities"
              search={{ create: 'true' }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-card border border-border text-foreground font-medium text-sm hover:bg-muted transition-colors"
            >
              <Target className="w-4 h-4" />
              <span>New Opportunity</span>
            </Link>
          </div>
        </div>

        {isLoading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {/* Key Metrics Grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Total Pipeline */}
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-primary" />
                  </div>
                  {totalPipeline > 0 && (
                    <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-full">
                      Active
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(totalPipeline)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Total Pipeline</p>
                </div>
              </div>

              {/* Weighted Pipeline */}
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <svg
                      className="w-5 h-5 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                      />
                    </svg>
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-bold text-foreground">
                    {formatCurrency(weightedPipeline)}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">Weighted Pipeline</p>
                </div>
              </div>

              {/* Open Leads */}
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                  {newLeadsCount > 0 && (
                    <span className="text-xs font-medium text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                      +{newLeadsCount} new
                    </span>
                  )}
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-bold text-foreground">{openLeadsCount}</p>
                  <p className="text-sm text-muted-foreground mt-1">Open Leads</p>
                </div>
              </div>

              {/* Win Rate */}
              <div className="bg-card rounded-xl border border-border p-5">
                <div className="flex items-center justify-between">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                </div>
                <div className="mt-4">
                  <p className="text-2xl font-bold text-foreground">{winRate}%</p>
                  <p className="text-sm text-muted-foreground mt-1">Win Rate (90 days)</p>
                </div>
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Pipeline by Stage */}
              <div className="bg-card rounded-xl border border-border">
                <div className="p-5 border-b border-border">
                  <h2 className="text-lg font-semibold text-foreground">Pipeline by Stage</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Current distribution across stages
                  </p>
                </div>
                <div className="p-5">
                  {/* Stage Breakdown */}
                  <div className="space-y-4">
                    {pipelineByStage.map(stage => (
                      <div key={stage.stage}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div className={`w-3 h-3 rounded-full ${stage.dotClass}`}></div>
                            <span className="text-sm font-medium text-foreground">
                              {stage.label}
                            </span>
                          </div>
                          <div className="text-right">
                            <span className="text-sm font-medium text-foreground">
                              {formatCurrency(stage.amount)}
                            </span>
                            <span className="text-xs text-muted-foreground ml-2">
                              ({stage.count})
                            </span>
                          </div>
                        </div>
                        <div className="h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${stage.dotClass}`}
                            style={{
                              width: `${totalPipeline > 0 ? Math.round((stage.amount / totalPipeline) * 100) : 0}%`,
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pipeline Summary */}
                  <div className="mt-6 pt-4 border-t border-border">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Total Open Deals</span>
                      <span className="font-semibold text-foreground">{openOpps.length} deals</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Closing This Month */}
              <div className="bg-card rounded-xl border border-border">
                <div className="p-5 border-b border-border flex items-center justify-between">
                  <div>
                    <h2 className="text-lg font-semibold text-foreground">Closing This Month</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                      Opportunities with close dates this month
                    </p>
                  </div>
                  <span className="text-xs font-medium bg-primary/10 text-primary px-2.5 py-1 rounded-full">
                    {closingThisMonth.length} deals
                  </span>
                </div>
                <div className="divide-y divide-border max-h-96 overflow-auto">
                  {closingThisMonth.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      <Calendar className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                      <p>No opportunities closing this month</p>
                    </div>
                  ) : (
                    closingThisMonth.map(opp => {
                      const stageInfo = stageConfig[opp.stage] || {
                        label: opp.stage,
                        bgClass: 'bg-muted',
                      };
                      return (
                        <Link
                          key={opp.id}
                          to="/opportunities/$id"
                          params={{ id: opp.id }}
                          className="block p-4 hover:bg-muted/50 transition-colors cursor-pointer"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-medium text-foreground truncate">{opp.name}</h3>
                              <p className="text-sm text-muted-foreground mt-0.5">
                                {opp.accountName || 'No Account'}
                              </p>
                            </div>
                            <div className="text-right ml-4">
                              <p className="font-semibold text-foreground">
                                {formatCurrency(opp.amount || 0)}
                              </p>
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${stageInfo.bgClass}`}
                              >
                                {stageInfo.label}
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Calendar className="w-3.5 h-3.5" />
                              <span>{formatShortDate(opp.expectedCloseDate)}</span>
                            </span>
                            <span
                              className={
                                opp.probability >= 75
                                  ? 'text-green-600 font-medium'
                                  : opp.probability >= 50
                                    ? 'text-amber-600 font-medium'
                                    : ''
                              }
                            >
                              {opp.probability}%
                            </span>
                          </div>
                        </Link>
                      );
                    })
                  )}
                </div>

                {/* View All Link */}
                <div className="p-4 border-t border-border">
                  <Link
                    to="/opportunities"
                    className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
                  >
                    View all opportunities
                    <ChevronRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>

            {/* New Leads This Week */}
            <div className="bg-card rounded-xl border border-border">
              <div className="p-5 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">Recent Leads</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    Latest leads in your pipeline
                  </p>
                </div>
                <Link
                  to="/leads"
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-foreground bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Lead
                </Link>
              </div>

              {/* Leads Table (Desktop) */}
              <div className="hidden sm:block overflow-x-auto">
                {recentLeads.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-3" />
                    <p>No leads yet</p>
                    <p className="text-sm mt-1">Add your first lead to get started</p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                          Name
                        </th>
                        <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                          Company
                        </th>
                        <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                          Status
                        </th>
                        <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                          Source
                        </th>
                        <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-5 py-3">
                          Created
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {recentLeads.map(lead => {
                        const fullName = `${lead.firstName} ${lead.lastName}`;
                        const status = statusConfig[lead.status] || {
                          label: lead.status,
                          class: 'bg-muted text-muted-foreground',
                        };
                        return (
                          <tr
                            key={lead.id}
                            className="hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => (window.location.href = `/leads/${lead.id}`)}
                          >
                            <td className="px-5 py-4">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-8 h-8 rounded-full flex items-center justify-center ${getAvatarColor(fullName)}`}
                                >
                                  <span className="text-xs font-medium">
                                    {getInitials(lead.firstName, lead.lastName)}
                                  </span>
                                </div>
                                <div>
                                  <p className="font-medium text-foreground">{fullName}</p>
                                  <p className="text-sm text-muted-foreground">
                                    {lead.email || '-'}
                                  </p>
                                </div>
                              </div>
                            </td>
                            <td className="px-5 py-4 text-sm text-foreground">
                              {lead.company || '-'}
                            </td>
                            <td className="px-5 py-4">
                              <span
                                className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${status.class}`}
                              >
                                {status.label}
                              </span>
                            </td>
                            <td className="px-5 py-4 text-sm text-muted-foreground">
                              {sourceLabels[lead.source] || lead.source}
                            </td>
                            <td className="px-5 py-4 text-sm text-muted-foreground">
                              {formatRelativeDate(lead.createdAt)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Mobile Cards View */}
              <div className="sm:hidden divide-y divide-border">
                {recentLeads.map(lead => {
                  const fullName = `${lead.firstName} ${lead.lastName}`;
                  const status = statusConfig[lead.status] || {
                    label: lead.status,
                    class: 'bg-muted text-muted-foreground',
                  };
                  return (
                    <Link
                      key={lead.id}
                      to="/leads/$id"
                      params={{ id: lead.id }}
                      className="block p-4 hover:bg-muted/50 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${getAvatarColor(fullName)}`}
                        >
                          <span className="text-sm font-medium">
                            {getInitials(lead.firstName, lead.lastName)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-foreground">{fullName}</p>
                              <p className="text-sm text-muted-foreground">{lead.company || '-'}</p>
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.class}`}
                            >
                              {status.label}
                            </span>
                          </div>
                          <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                            <span>{sourceLabels[lead.source] || lead.source}</span>
                            <span>·</span>
                            <span>{formatRelativeDate(lead.createdAt)}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>

              {/* View All Link */}
              <div className="p-4 border-t border-border">
                <Link
                  to="/leads"
                  className="text-sm text-primary font-medium hover:underline flex items-center gap-1"
                >
                  View all leads
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </AppLayout>
  );
}
